import { type OrderDto, REPORT_REASONS, TEXT_LIMITS } from '@posilka/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@posilka/client/api/client';
import { useOrderMutation } from '@posilka/client/api/orders';
import { Section } from '@/components/Section';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { haptic } from '@/lib/telegram';
import { CharHint } from '@/components/Field';

function ReviewForm({ order }: { order: OrderDto }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const submit = useOrderMutation(() =>
    api<OrderDto>(`/orders/${order.id}/review`, {
      method: 'POST',
      body: { rating, comment: comment.trim() || undefined },
    }),
  );
  const target = order.viewer.role === 'sender' ? order.driver?.firstName : order.sender?.firstName;

  return (
    <Section title={`${t('review.title')}${target ? `: ${target}` : ''}`}>
      <div className="space-y-3 p-4">
        <div className="flex justify-center gap-2" role="radiogroup">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={String(n)}
              onClick={() => {
                haptic.tap();
                setRating(n);
              }}
              className={`text-4xl transition-transform ${n <= rating ? '' : 'opacity-25 grayscale'} active:scale-90`}
            >
              ⭐
            </button>
          ))}
        </div>
        {rating > 0 && (
          <>
            <textarea
              className="input min-h-16 resize-none"
              placeholder={t('review.comment')}
              maxLength={TEXT_LIMITS.reviewComment.max}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <CharHint count={{ value: comment, ...TEXT_LIMITS.reviewComment }} />
            <button
              type="button"
              className="btn-primary w-full"
              disabled={submit.isPending}
              onClick={() =>
                submit.mutate(undefined, {
                  onSuccess: () => haptic.success(),
                  onError: () => haptic.error(),
                })
              }
            >
              {t('review.submit')}
            </button>
          </>
        )}
        {submit.isError && <p className="text-sm text-destructive">{errorMessage(submit.error)}</p>}
      </div>
    </Section>
  );
}

function ReportForm({ order }: { order: OrderDto }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<(typeof REPORT_REASONS)[number]>('other');
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const submit = useOrderMutation(() =>
    api<OrderDto>(`/orders/${order.id}/report`, {
      method: 'POST',
      body: { reason, text: text.trim() },
    }),
  );

  if (sent) return <p className="mb-4 px-1 text-sm text-hint">✅ {t('report.sent')}</p>;
  if (!open) {
    return (
      <button
        type="button"
        className="mb-3 w-full py-2 text-sm text-hint"
        onClick={() => setOpen(true)}
      >
        🚩 {t('report.title')} {t('report.open')}
      </button>
    );
  }
  return (
    <Section title={t('report.open')}>
      <div className="space-y-2 p-3">
        <select
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value as typeof reason)}
        >
          {REPORT_REASONS.map((r) => (
            <option key={r} value={r}>
              {t(`report.reasons.${r}`)}
            </option>
          ))}
        </select>
        <textarea
          className="input min-h-24 resize-none"
          placeholder={t('report.text')}
          maxLength={TEXT_LIMITS.report.max}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <CharHint count={{ value: text, ...TEXT_LIMITS.report }} />
        <div className="flex gap-2">
          <button type="button" className="btn-secondary flex-1" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn-secondary flex-1 text-destructive"
            disabled={text.trim().length < TEXT_LIMITS.report.min || submit.isPending}
            onClick={() =>
              submit.mutate(undefined, {
                onSuccess: () => {
                  haptic.success();
                  setSent(true);
                },
                onError: () => haptic.error(),
              })
            }
          >
            {t('report.submit')}
          </button>
        </div>
        {submit.isError && <p className="text-sm text-destructive">{errorMessage(submit.error)}</p>}
      </div>
    </Section>
  );
}

/** Baholash (DELIVERED, bir marta) va shikoyat (SPEC §11). */
export function OrderFeedback({ order }: { order: OrderDto }) {
  return (
    <>
      {order.viewer.canReview && <ReviewForm order={order} />}
      {order.viewer.canReport && <ReportForm order={order} />}
    </>
  );
}
